import React, { useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { BrokenImage as BrokenImageIcon } from '@mui/icons-material';
import { Outfit } from '../../types';

export interface OutfitSlideProps {
  outfit: Outfit;
  onClick: () => void; // pause/resume toggle
}

/**
 * Displays a single outfit image centered on the runway backdrop.
 *
 * The image is sized to max-height: 80vh with object-fit: contain so it
 * fills the available space while preserving aspect ratio. Clicking the
 * slide toggles pause/resume. If the image fails to load a placeholder
 * fallback is shown instead.
 *
 * Requirements: 8.1, 8.2, 8.3, 5.1
 */
const OutfitSlide: React.FC<OutfitSlideProps> = ({ outfit, onClick }) => {
  const [imageError, setImageError] = useState<boolean>(false);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageError(false);
  }, []);

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
        position: 'relative',
        zIndex: 1,
      }}
      role="button"
      tabIndex={0}
      aria-label={`Outfit: ${outfit.title || 'Fashion outfit'}. Click to toggle pause.`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {!imageError ? (
        <Box
          component="img"
          src={outfit.imageUrl}
          alt={outfit.title || outfit.description || 'Fashion outfit'}
          onError={handleImageError}
          onLoad={handleImageLoad}
          sx={{
            maxHeight: '80vh',
            maxWidth: '100%',
            objectFit: 'contain',
            borderRadius: 2,
            boxShadow: `
              0 0 15px 4px rgba(100, 180, 255, 0.4),
              0 0 30px 8px rgba(100, 180, 255, 0.2),
              0 0 60px 16px rgba(100, 180, 255, 0.1),
              0 8px 32px rgba(0, 0, 0, 0.5)
            `,
            border: '2px solid rgba(120, 200, 255, 0.35)',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 320,
            height: 480,
            maxHeight: '80vh',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.15)',
          }}
        >
          <BrokenImageIcon
            sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.4)', mb: 2 }}
          />
          <Box
            component="span"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.875rem',
              textAlign: 'center',
              px: 2,
            }}
          >
            Image unavailable
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OutfitSlide;
